/*
 * Envoi des décomptes (producteurs, prosumers) depuis la todolist. Les factures des consommateurs
 * passent par BillingRunDialog (canaux Accountable, e-mail, domiciliation).
 * Société : Accountable et/ou e-mail au membre. Particulier : e-mail uniquement.
 * E-mail au membre choisi ligne par ligne : coché par défaut pour un nouveau membre (première période, modèle « Premier décompte »),
 * modèle au choix, aperçu de l'e-mail réel et texte modifiable pour ce membre. Sans envoi, la date d'émission est enregistrée
 * pour l'espace membre et la tâche cochée.
 * Rien ne part sans validation : « Vérifier l'envoi » affiche ce qui va être fait, puis « Confirmer l'envoi ».
 * Chaque tâche envoyée est cochée dans la todolist.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, BookOpenCheck, CheckCircle2, Download, Loader2, Mail, Send, XCircle } from 'lucide-react';
import type { SharingOperation } from './types';
import type { TodoItem } from '../../../supabase/functions/_shared/crmTodo';
import {
  ACCOUNTABLE_ADDRESSES, type AccountingSettings, type AccountingTarget, DEFAULT_ACCOUNTING_SETTINGS, DOCUMENT_LABELS,
  type DocumentRecipient, blobToBase64, buildDocument, defaultMessageKind, defaultTarget, documentKindOf, downloadBlob, fetchAccountingSettings,
  fetchDocumentRecipients, fetchEntryMonths, isCompanyItem, isFirstPeriod, itemKey, renderDocumentMessage, saveIssueDate, sendAccountingDocument,
} from './accounting';
import { type MemberEmailChoice, MemberEmailControls } from './MemberEmailControls';
import { setTodoDone } from './api';
import { ModalShell, errorMessage, inputClass } from './ui';

type Status = 'idle' | 'generating' | 'sending' | 'sent' | 'error';

interface ItemState {
  status: Status;
  message?: string;
  /** Destinations déjà servies (un nouvel essai ne renvoie pas un document déjà reçu). */
  delivered: { accountable: boolean; member: boolean };
}

interface Props {
  items: TodoItem[];
  operations: SharingOperation[];
  onClose: () => void;
  /** Tâche envoyée et cochée. */
  onSent: (item: TodoItem) => void;
}

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EMPTY_STATE: ItemState = { status: 'idle', delivered: { accountable: false, member: false } };

export function SendDocumentsDialog({ items, operations, onClose, onSent }: Props) {
  const docKind = documentKindOf(items[0]);
  const labels = DOCUMENT_LABELS[docKind];
  const companies = items.filter(isCompanyItem);
  const individuals = items.length - companies.length;
  const [settings, setSettings] = useState<AccountingSettings | null>(null);
  const [recipients, setRecipients] = useState<Record<string, DocumentRecipient> | null>(null);
  const [toAccountable, setToAccountable] = useState(true);
  /** Mois d'entrée de chaque membre (itemKey) et e-mail au membre, ligne par ligne. */
  const [entryMonths, setEntryMonths] = useState<Record<string, string | null> | null>(null);
  const [emails, setEmails] = useState<Record<string, MemberEmailChoice>>({});
  /** Catégorie Accountable par document (proposée selon le type des EAN). */
  const [targets, setTargets] = useState<Record<string, AccountingTarget>>({});
  const [issueDate, setIssueDate] = useState(todayIso());
  const [selected, setSelected] = useState<Set<string>>(() => new Set(items.map(itemKey)));
  const [states, setStates] = useState<Record<string, ItemState>>({});
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState<string | null>(null);
  /** Étape de validation : récapitulatif de ce qui va partir, avant tout envoi. */
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    fetchAccountingSettings()
      .catch(err => { toast.error(`Réglages de comptabilité : ${errorMessage(err)}`); return DEFAULT_ACCOUNTING_SETTINGS; })
      .then(s => {
        setSettings(s);
        setToAccountable(s.sendToAccountable);
      });
    fetchEntryMonths(items)
      .catch(err => { toast.error(`Dates d’entrée des membres : ${errorMessage(err)}`); return {} as Record<string, string | null>; })
      .then(months => {
        setEntryMonths(months);
        // Par défaut : e-mail aux nouveaux membres seulement (première période), modèle « Premier décompte ».
        setEmails(Object.fromEntries(items.map(i => {
          const first = isFirstPeriod(i, months[itemKey(i)]);
          return [itemKey(i), { send: first, kind: defaultMessageKind({ statement: docKind === 'statement', first }), kindTouched: false, override: null }];
        })));
      });
    fetchDocumentRecipients(items)
      .then(r => {
        setRecipients(r);
        setTargets(Object.fromEntries(items.map(i => [itemKey(i), defaultTarget(i, r[itemKey(i)]?.roles ?? [])])));
      })
      .catch(err => { toast.error(`Adresses des membres : ${errorMessage(err)}`); setRecipients({}); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const opName = (id: string | null) => operations.find(o => o.id === id)?.name ?? '';
  const stateOf = (key: string): ItemState => states[key] ?? EMPTY_STATE;
  const patchState = (key: string, patch: Partial<ItemState>) =>
    setStates(prev => ({ ...prev, [key]: { ...(prev[key] ?? EMPTY_STATE), ...patch } }));
  const emailOf = (item: TodoItem) => recipients?.[itemKey(item)]?.email ?? null;
  const isFirst = (item: TodoItem) => isFirstPeriod(item, entryMonths?.[itemKey(item)]);
  const choiceOf = (key: string): MemberEmailChoice => emails[key]
    ?? { send: false, kind: defaultMessageKind({ statement: docKind === 'statement', first: false }), kindTouched: false, override: null };
  const setChoice = (key: string, p: Partial<MemberEmailChoice>) => setEmails(prev => ({ ...prev, [key]: { ...(prev[key] ?? choiceOf(key)), ...p } }));
  const wantsMail = (item: TodoItem) => choiceOf(itemKey(item)).send;
  const varsFor = (item: TodoItem, doc?: { invoiceNumber: string; netAmount: number }): Record<string, string> => ({
    nom: item.title, periode: item.periodLabel, operation: opName(item.operationId),
    numero_facture: doc?.invoiceNumber ?? '[numéro attribué à l’envoi]',
    montant: doc ? Math.abs(doc.netAmount).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '[montant calculé à l’envoi]',
  });
  const messageFor = (item: TodoItem, doc?: { invoiceNumber: string; netAmount: number }) => {
    const choice = choiceOf(itemKey(item));
    return renderDocumentMessage(choice.override ?? (settings ?? DEFAULT_ACCOUNTING_SETTINGS).messages[choice.kind], varsFor(item, doc));
  };

  const pending = items.filter(i => selected.has(itemKey(i)) && stateOf(itemKey(i)).status !== 'sent');
  const sentCount = items.filter(i => stateOf(itemKey(i)).status === 'sent').length;
  /** E-mail coché sans adresse : il ne partira pas. */
  const noAddress = useMemo(() => (recipients ? items.filter(i => wantsMail(i) && !emailOf(i)) : []),
    [items, recipients, emails]); // eslint-disable-line react-hooks/exhaustive-deps
  const periods = Array.from(new Set(items.map(i => i.periodLabel)));

  const blocker = !issueDate ? 'Indiquez la date d’émission'
    : pending.length === 0 ? 'Aucun document à envoyer'
      : !settings || !recipients || !entryMonths ? 'Chargement…'
        : pending.some(i => wantsMail(i) && (() => { const m = messageFor(i); return !m.subject.trim() || !m.body.trim(); })())
          ? 'Un e-mail coché a un objet ou un message vide' : null;

  /** Ce qui partira pour chaque document en attente, avec les destinations cochées. */
  const plan = pending.map(item => {
    const key = itemKey(item);
    const prev = stateOf(key);
    const email = emailOf(item);
    const target = targets[key] ?? defaultTarget(item, recipients?.[key]?.roles ?? []);
    const actions = [
      isCompanyItem(item) && toAccountable && !prev.delivered.accountable
        ? `Accountable, ${target === 'expense' ? 'dépenses' : 'revenus'} (${ACCOUNTABLE_ADDRESSES[target]})` : null,
      wantsMail(item) && email && !prev.delivered.member ? `e-mail à ${email}` : null,
    ].filter(Boolean) as string[];
    return { item, actions };
  });

  const toggleItem = (key: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const preview = async (item: TodoItem) => {
    setPreviewing(itemKey(item));
    try {
      const doc = await buildDocument(item, issueDate);
      await downloadBlob(doc.blob, doc.fileName);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPreviewing(null);
    }
  };

  const sendAll = async () => {
    if (blocker || !recipients) return;
    setConfirming(false);
    setRunning(true);
    let ok = 0;
    for (const item of pending) {
      const key = itemKey(item);
      const prev = stateOf(key);
      const email = emailOf(item);
      const company = isCompanyItem(item);
      const target = targets[key] ?? defaultTarget(item, recipients[key]?.roles ?? []);
      const needAccountable = company && toAccountable && !prev.delivered.accountable;
      const needMember = wantsMail(item) && !!email && !prev.delivered.member;
      if (wantsMail(item) && !email && !prev.delivered.member) {
        patchState(key, { status: 'error', message: 'E-mail coché mais pas d’adresse e-mail sur la fiche' });
        continue;
      }
      try {
        patchState(key, { status: 'generating', message: undefined });
        const doc = await buildDocument(item, issueDate);
        if (!needAccountable && !needMember) {
          // Rien à envoyer : document disponible dans l'espace membre (date d'émission), tâche cochée.
          if (item.operationId) await saveIssueDate(item.operationId, doc, { startMonth: item.periodStart, endMonth: item.periodEnd }, issueDate);
          await setTodoDone({ kind: item.kind, ref: item.ref, period: item.period }, true);
          patchState(key, { status: 'sent', message: 'Aucun envoi : disponible dans l’espace membre, tâche cochée' });
          onSent(item);
          ok += 1;
          continue;
        }
        const pdfBase64 = await blobToBase64(doc.blob);
        patchState(key, { status: 'sending' });
        const rendered = messageFor(item, doc);
        const res = await sendAccountingDocument({
          target,
          fileName: doc.fileName,
          pdfBase64,
          accountable: needAccountable,
          member: needMember ? { email: email!, subject: rendered.subject, message: rendered.body } : null,
        });
        const delivered = {
          accountable: prev.delivered.accountable || (needAccountable && res.accountable.success),
          member: prev.delivered.member || (needMember && res.member.success),
        };
        const errors = [
          needAccountable && !res.accountable.success ? `Accountable : ${res.accountable.error ?? 'échec'}` : null,
          needMember && !res.member.success ? `E-mail : ${res.member.error ?? 'échec'}` : null,
        ].filter(Boolean) as string[];
        if (errors.length > 0) {
          patchState(key, { status: 'error', message: errors.join(' · '), delivered });
          continue;
        }
        // Date d'émission visible dans l'espace membre, puis tâche cochée.
        try {
          if (item.operationId) await saveIssueDate(item.operationId, doc, { startMonth: item.periodStart, endMonth: item.periodEnd }, issueDate);
        } catch (err) {
          console.warn('Date d’émission non enregistrée :', errorMessage(err));
        }
        await setTodoDone({ kind: item.kind, ref: item.ref, period: item.period }, true);
        const sentTo = [
          delivered.accountable ? `Accountable (${target === 'expense' ? 'dépenses' : 'revenus'})` : null,
          delivered.member && email ? email : null,
        ].filter(Boolean).join(' et ');
        patchState(key, { status: 'sent', message: `Envoyé à ${sentTo}`, delivered });
        onSent(item);
        ok += 1;
      } catch (err) {
        patchState(key, { status: 'error', message: errorMessage(err) });
      }
    }
    setRunning(false);
    if (ok > 0) toast.success(`${ok} ${ok > 1 ? labels.many : labels.one} envoyé${ok > 1 ? 's' : ''}, tâche${ok > 1 ? 's' : ''} cochée${ok > 1 ? 's' : ''}`);
  };

  const close = () => {
    if (running) { toast('Envoi en cours : patientez jusqu’à la fin'); return; }
    onClose();
  };

  const statusCell = (key: string) => {
    const s = stateOf(key);
    if (s.status === 'generating') return <span className="flex items-center gap-1 text-neutral-500"><Loader2 className="w-3.5 h-3.5 animate-spin" />Génération du PDF…</span>;
    if (s.status === 'sending') return <span className="flex items-center gap-1 text-neutral-500"><Loader2 className="w-3.5 h-3.5 animate-spin" />Envoi…</span>;
    if (s.status === 'sent') return <span className="flex items-start gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" />{s.message}</span>;
    if (s.status === 'error') return <span className="flex items-start gap-1 text-red-700"><XCircle className="w-3.5 h-3.5 mt-px shrink-0" />{s.message}</span>;
    return null;
  };

  const who = [
    companies.length ? `${companies.length} société${companies.length > 1 ? 's' : ''}` : null,
    individuals ? `${individuals} particulier${individuals > 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(', ');

  return (
    <ModalShell
      title={`Envoyer ${items.length > 1 ? `les ${labels.many}` : `le ${labels.one}`}`}
      subtitle={`${who} · ${periods.join(', ')}`}
      onClose={close}
      width="max-w-3xl"
      zIndex="z-[80]"
      footer={(
        <>
          <span className="mr-auto self-center text-[11px] text-neutral-500">
            {running ? `Envoi ${sentCount} / ${sentCount + pending.length}…` : blocker && blocker !== 'Aucun document à envoyer' ? blocker : sentCount > 0 ? `${sentCount} envoyé${sentCount > 1 ? 's' : ''}` : ''}
          </span>
          {confirming ? (
            <button onClick={() => setConfirming(false)} className="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:bg-white">Modifier</button>
          ) : (
            <button onClick={close} disabled={running} className="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:bg-white disabled:opacity-40">
              {sentCount > 0 && pending.length === 0 ? 'Fermer' : 'Annuler'}
            </button>
          )}
          {confirming ? (
            <button onClick={sendAll} disabled={!!blocker || running}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-sm font-semibold text-neutral-900 disabled:opacity-40">
              <Send className="w-4 h-4" />Confirmer l’envoi ({pending.length})
            </button>
          ) : (
            <button onClick={() => setConfirming(true)} disabled={!!blocker || running}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-neutral-900 hover:bg-neutral-700 text-sm font-semibold text-white disabled:opacity-40">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Vérifier l’envoi{pending.length > 0 ? ` (${pending.length})` : ''}
            </button>
          )}
        </>
      )}
    >
      {confirming ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-neutral-900">Ce qui va être fait après confirmation</p>
          <ul className="border border-neutral-200 rounded-xl divide-y divide-neutral-100">
            {plan.map(({ item, actions }) => (
              <li key={itemKey(item)} className="px-3 py-2">
                <p className="text-sm text-neutral-900"><span className="font-medium">{item.title}</span> <span className="text-neutral-500">· {item.periodLabel}</span></p>
                <p className={`text-[11px] ${actions.length ? 'text-neutral-600' : 'text-amber-700'}`}>
                  {actions.length ? `PDF envoyé : ${actions.join(' et ')}` : wantsMail(item) && !emailOf(item)
                    ? 'E-mail coché sans adresse : ne partira pas' : 'Aucun envoi : date d’émission enregistrée pour l’espace membre, tâche cochée'}
                </p>
                {wantsMail(item) && emailOf(item) && <p className="text-[11px] text-neutral-500">Objet : « {messageFor(item).subject} »</p>}
              </li>
            ))}
          </ul>
          <ul className="text-[11px] text-neutral-600 space-y-1 list-disc pl-4">
            <li>Date d’émission imprimée et enregistrée pour l’espace membre : {issueDate.split('-').reverse().join('/')}.</li>
            <li>Chaque document envoyé coche sa tâche dans la todolist.</li>
          </ul>
        </div>
      ) : (
      <div className="space-y-5">
        {/* Destinations */}
        <div className="space-y-2">
          <p className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Destinations</p>
          {companies.length > 0 && (
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${toAccountable ? 'border-[#F8BD3D] bg-amber-50/60' : 'border-neutral-200'}`}>
              <input type="checkbox" className="mt-1" checked={toAccountable} onChange={e => setToAccountable(e.target.checked)} disabled={running} />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-neutral-900"><BookOpenCheck className="w-4 h-4 text-neutral-500" />Accountable <span className="text-[11px] font-normal text-neutral-500">· sociétés uniquement</span></span>
                <span className="block text-[11px] text-neutral-500 mt-0.5">
                  Revenus ({ACCOUNTABLE_ADDRESSES.revenue}) ou dépenses ({ACCOUNTABLE_ADDRESSES.expense}), au choix pour chaque document ci-dessous.
                  Expédié depuis <b className="font-medium text-neutral-700">{settings?.accountableSender ?? '…'}</b>, l’adresse du compte Accountable (CRM → Paramètres → Comptabilité).
                </span>
              </span>
            </label>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-neutral-200 bg-neutral-50/60 px-3 py-2.5 text-[11px] text-neutral-600">
            <span className="flex items-center gap-1.5">
              <Mail className="w-4 h-4 text-neutral-500" />
              E-mail au membre : <b>{items.filter(wantsMail).length}</b> / {items.length}, choisi ligne par ligne (par défaut : nouveaux membres)
            </span>
            <span className="ml-auto flex items-center gap-2">
              <button type="button" onClick={() => setEmails(prev => Object.fromEntries(items.map(i => [itemKey(i), { ...(prev[itemKey(i)] ?? choiceOf(itemKey(i))), send: true }])))}
                className="text-neutral-500 hover:text-neutral-900 hover:underline">Tout cocher</button>
              <button type="button" onClick={() => setEmails(prev => Object.fromEntries(items.map(i => [itemKey(i), { ...(prev[itemKey(i)] ?? choiceOf(itemKey(i))), send: isFirst(i) }])))}
                className="text-neutral-500 hover:text-neutral-900 hover:underline">Nouveaux membres</button>
              <button type="button" onClick={() => setEmails(prev => Object.fromEntries(items.map(i => [itemKey(i), { ...(prev[itemKey(i)] ?? choiceOf(itemKey(i))), send: false }])))}
                className="text-neutral-500 hover:text-neutral-900 hover:underline">Aucun</button>
            </span>
            <span className="basis-full text-[10px] text-neutral-400">
              Adresse de facturation de la fiche participant, sinon e-mail du compte. Envoyé depuis info@sunisup.be. Sans e-mail ni Accountable,
              la date d’émission est enregistrée pour l’espace membre et la tâche cochée.
            </span>
          </div>
          {noAddress.length > 0 && (
            <p className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
              E-mail coché sans adresse e-mail : {noAddress.map(i => i.title).join(', ')}.
            </p>
          )}
        </div>

        {/* Date d'émission */}
        <label className="block max-w-xs">
          <span className="block text-[11px] font-medium text-neutral-600 mb-1">Date d’émission</span>
          <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} disabled={running} className={`${inputClass} border-neutral-200`} />
          <span className="block text-[10px] text-neutral-400 mt-1">Imprimée sur le document (échéance à 30 jours) et enregistrée pour l’espace membre.</span>
        </label>

        {/* Documents */}
        <div className="space-y-2">
          <p className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">{labels.many[0].toUpperCase() + labels.many.slice(1)}</p>
          <ul className="border border-neutral-200 rounded-xl divide-y divide-neutral-100">
            {items.map(item => {
              const key = itemKey(item);
              const s = stateOf(key);
              const email = emailOf(item);
              const company = isCompanyItem(item);
              return (
                <li key={key} className="flex items-start gap-3 px-3 py-2.5">
                  <input type="checkbox" className="mt-1" checked={selected.has(key)} onChange={() => toggleItem(key)} disabled={running || s.status === 'sent'}
                    aria-label={`Inclure ${item.title}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-neutral-900">
                      <span className="font-medium">{item.title}</span> <span className="text-neutral-500">· {item.periodLabel}</span>
                      {!company && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-600">particulier</span>}
                    </p>
                    <p className="text-[11px] text-neutral-500 truncate">
                      {item.subtitle}
                      {' · '}
                      {recipients === null ? '…' : email ? email : <span className="text-amber-700">pas d’adresse e-mail</span>}
                    </p>
                    {company && toAccountable && (
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-neutral-500">
                        Accountable :
                        <select value={targets[key] ?? 'revenue'} onChange={e => setTargets(t => ({ ...t, [key]: e.target.value as AccountingTarget }))}
                          disabled={running || s.delivered.accountable} className="px-1.5 py-0.5 text-[11px] border border-neutral-200 rounded-md bg-white">
                          <option value="revenue">Revenus</option>
                          <option value="expense">Dépenses</option>
                        </select>
                      </div>
                    )}
                    {settings && (
                      <MemberEmailControls
                        title={item.title}
                        email={email}
                        choice={choiceOf(key)}
                        onChange={p => setChoice(key, p)}
                        templates={settings.messages}
                        vars={varsFor(item)}
                        sentAt={s.delivered.member ? 'oui' : null}
                        disabled={running}
                        first={isFirst(item)}
                      />
                    )}
                    <div className="text-[11px] mt-0.5">{statusCell(key)}</div>
                  </div>
                  <button onClick={() => preview(item)} disabled={!!previewing || running || !issueDate}
                    title="Télécharger le PDF pour le vérifier"
                    className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 whitespace-nowrap disabled:opacity-40">
                    {previewing === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Aperçu
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="text-[10px] text-neutral-400">
            Le PDF est celui de l’outil de factures (une facture par société dans un groupe). La génération prend quelques secondes par document.
            Catégorie Accountable proposée : revenus pour un consommateur, dépenses pour un producteur ou un prosumer.
          </p>
        </div>
      </div>
      )}
    </ModalShell>
  );
}
